import React from 'react';
import { Box, Skeleton, CircularProgress, LinearProgress, Typography } from '@mui/material';

interface LoadingSkeletonProps {
  /** Number of skeleton rows to show */
  rows?: number;
  /** Show a centered spinner instead of skeleton rows */
  spinner?: boolean;
  /** Height of each skeleton row */
  rowHeight?: number;
}

/**
 * Reusable loading placeholder.
 * Use `spinner` prop for full-page / section loading states.
 * Use the default skeleton rows for list/card content areas.
 */
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  rows = 3,
  spinner = false,
  rowHeight = 20,
}) => {
  if (spinner) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
        sx={{ color: 'var(--color-primary)' }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={rowHeight}
          sx={{
            mb: 1.5,
            borderRadius: 'var(--radius-sm)',
            width: i === rows - 1 ? '60%' : '100%',
          }}
          animation="wave"
        />
      ))}
    </Box>
  );
};

export default LoadingSkeleton;

export const LoadingState: React.FC<{ message?: string }> = ({ message }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
    <LinearProgress sx={{ width: 200 }} />
    {message && <Typography variant="body2" color="text.secondary">{message}</Typography>}
  </Box>
);
